import React, { createContext, useContext, useState, useEffect } from 'react';
import { refreshAccessToken, clearUserData } from '../utils/tokenRefresh';
import { apiRequest, apiGet, apiPost, apiPatch, apiDelete, apiUpload } from '../utils/api';
import { clearModelServiceKey } from '../utils/modelService';
import { getToken as getAuthStoreToken, setToken as setAuthStoreToken, clearToken as clearAuthStoreToken, beginLogout, endLogout } from '../stores/authStore';
import { setPending as setPendingAgreements, clearPending as clearPendingAgreements } from '../stores/agreementGateStore';

// Cache API URL to prevent re-reads of window.location
const API_URL = window.location.hostname === 'jpro.app' ?
  'https://api.jpro.app/api/v1' :
  import.meta.env.VITE_API_URL || 'https://api.jpro.app/api/v1';

const AuthContext = createContext();

// Single source of truth for transforming backend snake_case user data to frontend camelCase.
// Backend already masks sensitive fields (google_id/microsoft_id → 'connected' or null)
// and returns public_id (UUID) as `id` — no raw database IDs are exposed.
function transformUserData(user) {
    return {
        id: user.id,
        username: user.username,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        phone: user.phone,
        city: user.city,
        state: user.state,
        professionalIdentity: user.professional_identity,
        avatarUrl: user.avatar_url,
        createdAt: user.created_at,
        admin: Boolean(user.admin),
        role: user.role || 'user',
        allowedFeatures: user.allowed_features || [],
        lockedFeatures: user.locked_features || [],
        clientAdminEmail: user.client_admin_email || null,
        has_password: user.has_password,
        outlookConnected: user.outlook_connected || false,
        clientMembership: user.client_membership || null,
        clientMemberships: user.client_memberships || [],
        uiPreferences: user.ui_preferences || {},
        isClientManager: (user.client_memberships || []).some(cm => cm.client_role === 'client_manager'),
        isClientUser: (user.client_memberships || []).some(cm => cm.client_role === 'client_user'),
        isMultiClient: (user.client_memberships || []).length > 1,
        clientRoleName: user.client_membership?.client_role_name || null
    };
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(null);
    // Set when we could not renew the session for a reason that is NOT an
    // authentication failure (renewal rate limited, API briefly unreachable).
    // Kept separate from "signed out" so the sign-in screen can say so instead
    // of implying the session expired.
    const [authUnavailable, setAuthUnavailable] = useState(false);
    

    useEffect(() => {
        async function bootstrap() {
            try {
                // Access token lives in memory only. On fresh page loads the store is
                // empty — mint a new access token from the httpOnly refresh cookie silently.
                // If the cookie is missing/expired, the user simply isn't logged in.
                let accessToken = getAuthStoreToken();
                if (!accessToken) {
                    try {
                        accessToken = await refreshAccessToken({ silent: true });
                    } catch (refreshError) {
                        // Transient: renewal was rate limited or the API was
                        // briefly unreachable. We can't authenticate this load,
                        // but nothing here says the session ended, so keep the
                        // cached profile and flag it as our problem, not a
                        // sign-out. A reload once it clears restores the session.
                        console.warn('Session renewal unavailable during bootstrap:', refreshError);
                        setAuthUnavailable(true);
                        return;
                    }
                }

                if (!accessToken) {
                    // No valid session. Drop any stale cached user and stay on public routes.
                    localStorage.removeItem('jpro_user');
                    return;
                }

                // Validate by fetching fresh profile data (includes updated permissions).
                const response = await apiGet('/profile');
                if (response.ok) {
                    const data = await response.json();
                    const freshUser = transformUserData(data.user);
                    setCurrentUser(freshUser);
                    setToken(accessToken);
                    setIsAuthenticated(true);
                    localStorage.setItem('jpro_user', JSON.stringify(freshUser));
                } else if (response.status === 423) {
                    // 423 Locked = token valid, user has pending agreements.
                    // api.js already seeded agreementGateStore from the response body.
                    // Hydrate auth from cached user blob so the modal renders over
                    // an authenticated shell instead of bouncing to /login.
                    const cached = localStorage.getItem('jpro_user');
                    if (cached) {
                        try {
                            const freshUser = JSON.parse(cached);
                            setCurrentUser(freshUser);
                            setToken(accessToken);
                            setIsAuthenticated(true);
                        } catch {
                            localStorage.removeItem('jpro_user');
                        }
                    }
                } else {
                    // Token validation failed (token revoked, account disabled, etc.)
                    clearAuthStoreToken();
                    localStorage.removeItem('jpro_user');
                }
            } catch (error) {
                console.error('Auth bootstrap error:', error);
                clearAuthStoreToken();
                localStorage.removeItem('jpro_user');
            } finally {
                setLoading(false);
            }
        }
        
        bootstrap();
    }, []);

    // Shared success path — used by email/password login, signup, Google,
    // Microsoft, and 2FA completion. Keeps all auth success routes in lockstep:
    // clear old state → hydrate fresh user → seed token + agreement gate.
    const applyAuthSuccess = (data) => {
        const rawUser = data.user;
        const finalAccessToken = data.access_token || data.token;
        if (!rawUser || !finalAccessToken) {
            throw new Error('Auth response missing user or access token');
        }

        // Clear any residual account-mixing state FIRST
        setCurrentUser(null);
        setToken(null);
        setIsAuthenticated(false);
        clearUserData();
        document.cookie = 'jpro_current_chat_id=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        document.cookie = 'jpro_current_session=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        document.cookie = 'jpro_chat_type=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';

        const transformedUser = transformUserData(rawUser);
        setCurrentUser(transformedUser);
        setToken(finalAccessToken);
        setIsAuthenticated(true);
        localStorage.setItem('jpro_user', JSON.stringify(transformedUser));
        setAuthStoreToken(finalAccessToken);
        setPendingAgreements(Array.isArray(data.pending_agreements) ? data.pending_agreements : []);

        return transformedUser;
    };

    // Called by the 2FA verification screen after a successful OTP / backup code.
    // The backend response shape matches the direct-login shape, so we just
    // funnel it through the same success path.
    const completeTwoFactor = (data) => {
        applyAuthSuccess(data);
    };

    const login = async (credentials) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ auth: credentials }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }

            // Check if 2FA is required
            if (data.requires_2fa) {
                return {
                    success: true,
                    requires_2fa: true,
                    temp_token: data.temp_token,
                    otp_method: data.otp_method,
                    secondary_method: data.secondary_method,
                    secondary_destination_mask: data.secondary_destination_mask,
                    // The OTP screen offers "skip the code on this device" only
                    // when these arrive; dropping them hid the option entirely.
                    device_trust_enabled: data.device_trust_enabled,
                    device_trust_days: data.device_trust_days,
                    user: data.user
                };
            }

            // Org policy enforces 2FA but the user hasn't set it up yet.
            // Caller routes to a forced-setup wizard; setup_token only allows /two_factor/* endpoints.
            if (data.requires_2fa_setup) {
                return {
                    success: true,
                    requires_2fa_setup: true,
                    setup_token: data.setup_token,
                    user: data.user
                };
            }

            applyAuthSuccess(data);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        } finally {
            setLoading(false);
        }
    };

    const signup = async (userData) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    // Honeypot — always empty for real users, populated by dumb bots.
                    website: userData.website || '',
                    user: {
                        username: userData.username,
                        email: userData.email,
                        password: userData.password,
                        password_confirmation: userData.passwordConfirmation,
                        first_name: userData.firstName,
                        last_name: userData.lastName,
                        phone: userData.phone,
                        city: userData.city,
                        state: userData.state
                    },
                    accepted_agreements: Array.isArray(userData.acceptedAgreements) ? userData.acceptedAgreements : []
                }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.errors?.join(', ') || 'Registration failed');
            }

            applyAuthSuccess(data);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        } finally {
            setLoading(false);
        }
    };

    const updateProfile = async (profileData) => {
        setLoading(true);
        try {
            // Using apiPatch with automatic token refresh
            const response = await apiPatch('/profile', {
                user: {
                    username: profileData.username,
                    email: profileData.email,
                    first_name: profileData.firstName,
                    last_name: profileData.lastName,
                    phone: profileData.phone,
                    city: profileData.city,
                    state: profileData.state,
                    professional_identity: profileData.professionalIdentity,
                    password: profileData.password,
                    password_confirmation: profileData.passwordConfirmation
                }
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.errors?.join(', ') || 'Profile update failed');
            }
            
            // Transform API response to match our app's user object structure
            const transformedUser = {
                id: data.user.id,
                username: data.user.username,
                firstName: data.user.first_name,
                lastName: data.user.last_name,
                email: data.user.email,
                phone: data.user.phone,
                city: data.user.city,
                state: data.user.state,
                professionalIdentity: data.user.professional_identity,
                avatarUrl: data.user.avatar_url,
                createdAt: data.user.created_at,
                admin: data.user.admin,
                role: data.user.role || 'user',
                allowedFeatures: data.user.allowed_features || [],
                lockedFeatures: data.user.locked_features || [],
                clientAdminEmail: data.user.client_admin_email || null,
                uiPreferences: data.user.ui_preferences || {}
            };
            
            setCurrentUser(transformedUser);
            
            // Update localStorage
            localStorage.setItem('jpro_user', JSON.stringify(transformedUser));
            
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.message 
            };
        } finally {
            setLoading(false);
        }
    };

    const updateAvatar = async (formData) => {
        setLoading(true);
        try {
            // Using apiUpload with automatic token refresh
            const response = await apiUpload('/profile/avatar', formData);
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Avatar update failed');
            }
            
            // Update just the avatar URL in the current user object
            const updatedUser = {
                ...currentUser,
                avatarUrl: data.avatar_url
            };
            
            setCurrentUser(updatedUser);
            
            // Update localStorage
            localStorage.setItem('jpro_user', JSON.stringify(updatedUser));
            
            return { success: true, avatarUrl: data.avatar_url };
        } catch (error) {
            return { 
                success: false, 
                error: error.message 
            };
        } finally {
            setLoading(false);
        }
    };

    const updatePassword = async (passwordData) => {
        // Don't set global loading state for password updates
        // setLoading(true);
        try {
            // Using apiPatch with automatic token refresh
            const response = await apiPatch('/profile/password', {
                user: {
                    current_password: passwordData.currentPassword,
                    password: passwordData.password,
                    password_confirmation: passwordData.passwordConfirmation
                }
            });

            const data = await response.json();
            
            if (!response.ok) {
                const errorMessage = data.errors?.join(', ') || data.error || 'Password update failed';
                return { 
                    success: false, 
                    error: errorMessage 
                };
            }
            
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.message 
            };
        }
        // Don't modify global loading state
        // finally {
        //     setLoading(false);
        // }
    };

    const logout = async () => {
        const authToken = token || getAuthStoreToken();

        // Mark logout in progress so trailing/in-flight requests that 401 against
        // the just-revoked session don't redirect to /login?expired=true (which
        // wrongly shows "Your session has expired" after an intentional logout).
        beginLogout();

        // Call backend logout to blacklist token
        if (authToken) {
            try {
                // Using apiPost with automatic token refresh
                const response = await apiPost('/auth/logout', {}, { credentials: 'include' });

                if (response.ok) {
                    console.log('Token successfully blacklisted on backend');
                } else {
                    console.warn('Backend logout failed, but proceeding with frontend logout');
                }
            } catch (error) {
                console.error('Logout API call failed:', error);
                // Continue with frontend logout even if backend call fails
            }
        }
        
        // Clear frontend authentication state
        setCurrentUser(null);
        setToken(null);
        setIsAuthenticated(false);
        clearPendingAgreements();

        // Clear all user-specific data from localStorage
        clearUserData();
        clearModelServiceKey();

        // Clear any cookies that might contain chat session data
        document.cookie = 'jpro_current_chat_id=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        document.cookie = 'jpro_current_session=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        document.cookie = 'jpro_chat_type=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';

        // Re-arm expired-session detection after any trailing requests settle, so
        // a genuine expiry later still shows the message. (A full-page navigation
        // would reset this anyway; this covers the SPA redirect-to-/login case.)
        setTimeout(endLogout, 4000);
    };

    // Google login function
    const googleLogin = async (credentialResponse) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/auth/google`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    credential: credentialResponse.credential
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Google login failed');
            }

            // Check if 2FA is required
            if (data.requires_2fa) {
                return {
                    success: true,
                    requires_2fa: true,
                    temp_token: data.temp_token,
                    otp_method: data.otp_method,
                    secondary_method: data.secondary_method,
                    secondary_destination_mask: data.secondary_destination_mask,
                    // The OTP screen offers "skip the code on this device" only
                    // when these arrive; dropping them hid the option entirely.
                    device_trust_enabled: data.device_trust_enabled,
                    device_trust_days: data.device_trust_days,
                    user: data.user
                };
            }

            // Forced 2FA setup (org policy)
            if (data.requires_2fa_setup) {
                return {
                    success: true,
                    requires_2fa_setup: true,
                    setup_token: data.setup_token,
                    user: data.user
                };
            }

            applyAuthSuccess(data);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        } finally {
            setLoading(false);
        }
    };

    // Function to get authorization headers for API requests
    const getAuthHeaders = async () => {
        // Use the current token or get it from localStorage
        const authToken = token || getAuthStoreToken();
        
        if (!authToken) {
            throw new Error('No authentication token found');
        }
        
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        };
    };
    
    // Function to handle API responses with token expiration and auto-refresh
    const handleApiResponse = async (response, originalRequest) => {
        if (response.status === 401) {
            console.log('⚠️ Received 401, attempting token refresh...');

            // Attempt to refresh token. A transient failure (renewal rate
            // limited, API briefly unreachable) throws and is deliberately left
            // to propagate: the session is still valid, so the caller should see
            // a retryable error rather than be logged out below.
            const newAccessToken = await refreshAccessToken();

            if (newAccessToken) {
                console.log('✅ Token refreshed, retrying original request');

                // If we have the original request info, retry it
                if (originalRequest) {
                    const { url, options } = originalRequest;
                    const retryOptions = {
                        ...options,
                        headers: {
                            ...options.headers,
                            'Authorization': `Bearer ${newAccessToken}`
                        }
                    };

                    // Retry the request with new token
                    const retryResponse = await fetch(url, retryOptions);
                    return retryResponse;
                }

                // If no original request info provided, just return the response
                // The caller will need to retry manually with the new token
                return response;
            } else {
                // Refresh failed - logout user
                console.error('❌ Token refresh failed, logging out');
                logout();
                window.location.href = '/login?expired=true';
                throw new Error('Your session has expired. Please log in again.');
            }
        }
        return response;
    };

    // Switch active client — re-fetches profile with new client context
    const switchClient = async (clientId) => {
        try {
            const response = await apiGet(`/profile?active_client_id=${clientId}`);
            if (response.ok) {
                const data = await response.json();
                const updatedUser = transformUserData(data.user);
                setCurrentUser(updatedUser);
                localStorage.setItem('jpro_user', JSON.stringify(updatedUser));
            }
        } catch (err) {
            console.error('Failed to switch client:', err);
        }
    };

    // Re-fetch the user profile from the server (e.g., after subscription changes)
    const refreshUser = async () => {
        try {
            const response = await apiGet('/profile');
            if (response.ok) {
                const data = await response.json();
                const updatedUser = transformUserData(data.user);
                setCurrentUser(updatedUser);
                localStorage.setItem('jpro_user', JSON.stringify(updatedUser));
            }
        } catch (err) {
            console.error('Failed to refresh user:', err);
        }
    };

    // Universal feature access check (database-driven)
    const canAccess = (featureKey) => {
        if (!currentUser) return false;
        return (currentUser.allowedFeatures || []).includes(featureKey);
    };

    // Locked-but-visible: user can't access but admin opted feature into showcase
    const isLocked = (featureKey) => {
        if (!currentUser) return false;
        return (currentUser.lockedFeatures || []).some(f => f.key === featureKey);
    };

    // Why this feature is locked for this user: 'subscription' | 'role' | 'client' | null
    const getLockReason = (featureKey) => {
        if (!currentUser) return null;
        return (currentUser.lockedFeatures || []).find(f => f.key === featureKey)?.lock_reason || null;
    };

    // Backward-compatible wrappers — delegate to canAccess.
    // PDF Extraction was consolidated into JPRO Intake (data_extraction).
    const canAccessPdfExtraction = () => canAccess('data_extraction');
    const canAccessAit = () => canAccess('ait');
    const canAccessTeamChat = () => canAccess('team_chat');

    const isAdmin = () => {
        if (!currentUser) return false;
        return currentUser.admin === true || currentUser.role === 'admin';
    };

    const isManager = () => {
        if (!currentUser) return false;
        return currentUser.role === 'manager';
    };

    const isAssociate = () => {
        if (!currentUser) return false;
        return currentUser.role === 'associate';
    };

    const updateUiPreferences = async (patch) => {
        try {
            const response = await apiPatch('/profile/ui_preferences', patch);
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to update preferences');

            const merged = { ...(currentUser?.uiPreferences || {}), ...(data.preferences || {}) };
            const updatedUser = { ...currentUser, uiPreferences: merged };
            setCurrentUser(updatedUser);
            localStorage.setItem('jpro_user', JSON.stringify(updatedUser));
            return { success: true, preferences: merged };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    return (
        <AuthContext.Provider value={{
            currentUser,
            isAuthenticated,
            loading,
            token,
            authUnavailable,
            login,
            completeTwoFactor,
            signup,
            logout,
            updateProfile,
            updateAvatar,
            updatePassword,
            googleLogin,
            getAuthHeaders,
            handleApiResponse,
            canAccess,
            isLocked,
            getLockReason,
            refreshUser,
            switchClient,
            canAccessPdfExtraction,
            canAccessAit,
            canAccessTeamChat,
            isAdmin,
            isManager,
            isAssociate,
            updateUiPreferences,
            // API utility functions with auto token refresh
            apiRequest,
            apiGet,
            apiPost,
            apiPatch,
            apiDelete,
            apiUpload
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        // Happens when a component renders outside <AuthProvider>, or occasionally
        // during Vite HMR when context references drift. A hard refresh clears
        // the HMR case; outside-of-provider needs a component tree fix.
        throw new Error('useAuth must be used within an <AuthProvider>');
    }
    return ctx;
}